/**
 * Chat Message Component
 * Displays individual chat messages (user or AI)
 * Enhanced with better spacing and optional timestamp
 */

import {ChatMessage as ChatMessageType} from '@voxapilot/shared';
import {cn} from '../../utils/cn';

interface ChatMessageProps {
  message: ChatMessageType;
  showTimestamp?: boolean;
}

export function ChatMessage({message, showTimestamp = false}: ChatMessageProps) {
  const isUser = message.role === 'user';

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };

  return (
    <div
      className={cn(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div className={cn('max-w-[80%]', 'flex flex-col', isUser ? 'items-end' : 'items-start', 'animate-fade-in')}>
        <div
          className={cn(
            'rounded-lg px-4 py-3 shadow-sm transition-all duration-200',
            isUser
              ? 'bg-orange-600 dark:bg-orange-500 text-white shadow-orange-600/10 dark:shadow-orange-500/10'
              : 'bg-white dark:bg-[#252526] text-textPrimary dark:text-[#CCCCCC] border border-gray-200/60 dark:border-gray-700/60 shadow-gray-900/5 dark:shadow-gray-900/20'
          )}
        >
          {/* Message Content */}
          <div className="text-base leading-relaxed whitespace-pre-wrap break-words">
            {message.content}
          </div>
        </div>
        {/* Timestamp */}
        {showTimestamp && message.metadata?.timestamp && (
          <span
            className={cn(
              'text-xs mt-2 px-1.5',
              isUser
                ? 'text-gray-500 dark:text-gray-400'
                : 'text-gray-400 dark:text-gray-500'
            )}
          >
            {formatTimestamp(message.metadata.timestamp)}
          </span>
        )}
      </div>
    </div>
  );
}

